<template>
  <div
    class="absolute inset-0 w-screen h-screen flex items-center justify-center z-10"
  >
    <div
      class="w-[95%] sm:w-[90%] max-w-[763px] p-4 sm:p-8 relative mx-auto my-auto shadow-lg bg-white rounded-[10px] z-10 overflow-y-auto max-h-[95vh]"
    >
      <button
        aria-label="Close modal"
        class="absolute top-2 right-2 sm:top-4 sm:right-4 w-12 h-12 sm:w-8 sm:h-8 flex items-center justify-center text-black hover:text-gray-600 transition-colors z-50"
        @click="emit('close')"
      >
        <svg
          class="w-7 h-7 sm:w-5 sm:h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          stroke-width="2.5"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>

      <div class="text-center mb-4 sm:mb-6">
        <button
          aria-label="Go back"
          class="absolute left-2 top-2 sm:left-4 sm:top-4 flex items-center text-black hover:text-gray-600 transition-colors z-50 w-12 h-12 sm:w-auto sm:h-auto justify-center sm:justify-start"
          @click="emit('back')"
        >
          <svg
            class="w-7 h-7 sm:w-4 sm:h-4 sm:mr-1"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            stroke-width="2.5"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>

        <h2 class="text-xl sm:text-[32px] font-bold text-black mb-2 px-4">
          Felt Right's Gallery Designs
        </h2>
        <p class="text-xs sm:text-sm font-semibold text-black px-4">
          Browse, pick, and customize your favorite.
        </p>
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        <div
          v-for="design in gallery.designs"
          :key="design.id"
          class="relative"
        >
          <div
            class="w-full cursor-pointer mb-2"
            @click="handleOpenInStudio(design.id)"
          >
            <div
              v-if="design.image || design.shopify_image_url"
              class="relative w-full aspect-square bg-gray-100 rounded-lg overflow-hidden"
            >
              <img
                :src="
                  getOptimizedImageUrl(design.image || design.shopify_image_url)
                "
                alt=""
                class="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
                :class="{
                  'opacity-0': !loadedImages[design.id],
                  'opacity-100 transition-opacity duration-300':
                    loadedImages[design.id],
                }"
              />
            </div>

            <div
              v-if="!design.image && !design.shopify_image_url"
              class="w-full aspect-square bg-gray-100 rounded-lg overflow-hidden"
            >
              <v-stage
                :config="{
                  ...tempStageConfig,
                  id: design.id,
                }"
              >
                <v-layer :config="{ id: 'shapes-layer', name: 'shapes-layer' }">
                  <component
                    :is="getShapeComponent(shape.type)"
                    v-for="shape in design.shapeList"
                    :key="shape.id"
                    :config="shape"
                    :draggable="false"
                  />
                </v-layer>
              </v-stage>
            </div>
          </div>

          <div class="mb-2">
            <h3
              v-if="design.name"
              class="text-sm font-bold text-black truncate"
            >
              {{ design.name }}
            </h3>
            <p
              v-if="design.gridWidth && design.gridHeight"
              class="text-[10px] text-black"
            >
              {{ (design.gridWidth / 72).toFixed(2) }}in x
              {{ (design.gridHeight / 72).toFixed(2) }}in
            </p>
          </div>

          <button
            aria-label="Customize design"
            class="w-full bg-[#2c3e50] text-white py-2.5 sm:py-2 px-4 rounded-full text-xs font-medium hover:bg-[#1a252f] transition-colors min-h-[44px] sm:min-h-0"
            @click="handleOpenInStudio(design.id)"
          >
            Customize
          </button>

          <div
            v-if="
              gallery.designs.indexOf(design) % 2 === 0 &&
              gallery.designs.indexOf(design) < gallery.designs.length - 1
            "
            class="hidden sm:block absolute right-[-8px] top-0 w-[1px] h-full bg-gray-300"
          />
        </div>

        <div
          v-if="gallery.currentPage < gallery.totalPages"
          class="col-span-1 sm:col-span-2"
        >
          <button
            aria-label="Load more designs"
            class="w-full bg-black text-white py-3 sm:py-2 px-4 rounded-full text-sm font-medium hover:bg-gray-800 transition-colors mt-4 min-h-[44px] sm:min-h-0"
            :disabled="gallery.isLoading"
            @click="loadMore"
          >
            {{ gallery.isLoading ? "Loading..." : "Load More" }}
          </button>
        </div>

        <div
          v-if="gallery.currentPage >= gallery.totalPages"
          class="col-span-1 sm:col-span-2 text-center text-xs text-gray-600 mt-4"
        >
          All Designs Loaded
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { SELECTABLE_TOOLS } from "~/composables/grid/constants";
import { getShapeComponent } from "~/components/CanvasGrid/helpers";
import { SweetAlertIcon } from "~/types/sweetalert";

const emit = defineEmits(["close", "back"]);

const { $toast } = useNuxtApp();
const gridStore = useGridStore();
const projectStore = useProjectStore();
const loadingStore = useLoadingStore();
const gallery = useGalleryStore();
const { isMobile } = useDeviceDetection();

const loadedImages = ref({});

const tempStageConfig = {
  width: 820,
  height: 400,
  pixelRatio: 1,
  scaleX: 0.5,
  scaleY: 0.5,
};

const getOptimizedImageUrl = (url) => {
  if (!url || !url.includes("cloudinary.com")) return url;
  if (url.includes("/w_")) return url;
  return url.replace("/upload/", "/upload/w_600/");
};

const loadGalleryImages = (startIndex = 0) => {
  gallery.designs.slice(startIndex).forEach((design, index) => {
    setTimeout(() => {
      const imageUrl = design.image || design.shopify_image_url;
      if (imageUrl && !loadedImages.value[design.id]) {
        const img = new Image();
        img.src = getOptimizedImageUrl(imageUrl);
        img.onload = () => {
          loadedImages.value[design.id] = true;
        };
      }
    }, index * 100);
  });
};

const handleOpenInStudio = async (designID) => {
  if (!designID) return;
  try {
    loadingStore.setLoading(true);
    const design = await projectStore.load(designID);
    if (!design) throw new Error("Design not found");
    gridStore.resetGrid();
    await gridStore.loadDesign(design);
    setCookie("studio_design_id", designID);
    emit("close");
  } catch {
    $toast.fire({
      icon: SweetAlertIcon.ERROR,
      text: "Error loading design",
    });
  } finally {
    if (isMobile.value) {
      gridStore.updateSelectedTool(SELECTABLE_TOOLS.MOVE_GRID);
    } else if (projectStore.designId) {
      gridStore.updateSelectedTool(SELECTABLE_TOOLS.SELECT);
    } else {
      gridStore.updateSelectedTool(SELECTABLE_TOOLS.ADD);
    }
    loadingStore.setLoading(false);
  }
};

const loadMore = async () => {
  const previousLength = gallery.designs.length;
  await gallery.loadMore();
  loadGalleryImages(previousLength);
};

onMounted(() => {
  gallery.loadDesigns().then(() => {
    loadGalleryImages();
  });
});

onUnmounted(() => {
  loadedImages.value = {};
});
</script>
